import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import {
  selectActionList,
  selectCurrentVariantCohort,
  selectSubscriptionList,
} from 'root-redux/selects/common'
import {
  GET_SUBSCRIPTION_LIST,
  getSubscriptionListAction,
  setScreenNameAction,
  startFetching,
} from 'root-redux/actions/common'
import { CHECK_PAYMENT_REQUEST_BUTTON } from 'modules/purchase/redux/actions'
import { selectSubscription } from 'modules/purchase/redux/selects'
import { selectUUID } from 'root-redux/selects/user'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { eventLogger } from 'services/eventLogger.service'
import { createProductId } from 'helpers/createProductId'
import { PagePath, ScreenName, SubscriptionListType } from 'root-constants'
import { ISubscription } from 'models/subscriptions.model'
import { Spinner } from 'components/Spinner'
import { SvgImage } from 'components/SvgImage'
import heroImage from 'assets/images/purchase-hero.jpg'
import checkIcon from 'assets/images/sprite/check-icon.svg'
import { WHAT_USER_GET_ITEMS } from 'modules/purchase/constants'
import { TAppDispatch } from 'root-redux/store/store'
import { Checkout } from 'modules/purchase/pages/Checkout'
import { Container } from 'components/Container'
import { StyledPurchaseVariant1 as S } from './PurchaseVariant1.styles'
import { SecurityInfo } from '../../components/SecurityInfo'
import { CustomerReviewsList } from '../../components/CustomerReviewsList'
import { FeaturedBlock } from '../../components/FeaturedBlock'
import { SubscriptionsBlock } from '../../components/SubscriptionsBlock'
import { SubheaderWithTimer } from '../../components/SubheaderWithTimer'

export const PurchaseVariant1: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const buttonElementRef = useRef<HTMLDivElement>(null)
  const uuid = useSelector(selectUUID)
  const cohort = useSelector(selectCurrentVariantCohort)
  const subscriptions = useSelector(selectSubscriptionList)
  const selectedSubscription = useSelector(selectSubscription)
  const fetchingActionsList = useSelector(selectActionList)
  const [isCheckoutShown, setIsCheckoutShown] = useState(false)

  const arePricesReady = useMemo(
    () => !fetchingActionsList?.includes(GET_SUBSCRIPTION_LIST),
    [fetchingActionsList],
  )

  useEffect(() => {
    dispatch(setScreenNameAction(ScreenName.ONBOARDING))
  }, [dispatch])

  useEffect(() => {
    if (!cohort) return

    dispatch(
      getSubscriptionListAction({
        cohort,
        type: SubscriptionListType.PURCHASE,
      }),
    )
  }, [dispatch, cohort])

  useEffect(() => {
    if (!subscriptions.length) return

    eventLogger.logSalePageShown({
      productIds: subscriptions.map(
        ({ periodName, periodQuantity, currentPrice }) =>
          createProductId({
            periodName,
            periodQuantity,
            price: currentPrice,
          }),
      ),
      screenName: ScreenName.ONBOARDING,
    })
  }, [subscriptions])

  const handleShowPayment = useCallback(() => {
    dispatch(startFetching(CHECK_PAYMENT_REQUEST_BUTTON))
    setIsCheckoutShown(true)

    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    googleAnalyticsLogger.logPageView(`${PagePath.CHECKOUT}/${cohort}`)
    googleAnalyticsLogger.logAddingToCart(selectedSubscription as ISubscription)
  }, [dispatch, selectedSubscription, uuid, cohort])

  return !arePricesReady ? (
    <Spinner />
  ) : (
    <>
      {!isCheckoutShown && (
        <S.Wrapper>
          <S.ProgramPlanContainer>
            <S.Title>
              <Trans i18nKey="purchase.title" components={[<strong />]} />
            </S.Title>
            <S.Hero>
              <img src={heroImage} alt="girl" />
            </S.Hero>
            <S.Received>
              <S.ListTitle>{t('purchase.listTitle')}</S.ListTitle>
              <ul>
                {WHAT_USER_GET_ITEMS.map((item) => (
                  <S.ListItem key={item.id}>
                    <SvgImage svg={checkIcon} width={30} />
                    {t(item.value as any)}
                  </S.ListItem>
                ))}
              </ul>
            </S.Received>
          </S.ProgramPlanContainer>
          <SubheaderWithTimer
            elemForComparisonRef={buttonElementRef}
            onButtonClick={handleShowPayment}
          />
          <Container>
            <SubscriptionsBlock
              onButtonClick={handleShowPayment}
              elemForComparisonRef={buttonElementRef}
            />
            <S.FeaturedBlockContainer>
              <FeaturedBlock />
            </S.FeaturedBlockContainer>
            <CustomerReviewsList />
            <SecurityInfo />
            <S.Button type="button" onClick={handleShowPayment}>
              {t('actions.startNow')}
            </S.Button>
          </Container>
        </S.Wrapper>
      )}
      {isCheckoutShown && <Checkout setIsCheckoutShown={setIsCheckoutShown} />}
    </>
  )
}
